/**
 * Unified marketing/analytics tracking.
 *
 * - Meta Pixel (fbq): lazy-loaded ONLY after marketing consent. Pixel id
 *   from VITE_META_PIXEL_ID (no-op when unset).
 * - GA4 (Firebase Analytics logEvent): gated by analytics consent.
 * - Single `track(event, params)` API fans out to both destinations.
 *
 * Conversion events (see attribution plan):
 *   landing_view              -> Pixel PageView + ViewContent | GA4 landing_view
 *   signup_started            -> Pixel InitiateCheckout       | GA4 signup_started
 *   account_created           -> Pixel CompleteRegistration   | GA4 account_created
 *   business_setup_completed  -> Pixel Lead (with event_id)   | GA4 business_setup_completed
 */
import { watch } from 'vue'
import { logEvent } from 'firebase/analytics'
import { analytics } from './firebase'
import { useCookieConsent } from '@/composables/useCookieConsent'

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void
    _fbq?: unknown
  }
}

const META_PIXEL_ID: string = import.meta.env.VITE_META_PIXEL_ID || ''
const GA_DEBUG: boolean = import.meta.env.VITE_GA_DEBUG === 'true'

let pixelLoaded = false

/** Inject the Meta Pixel snippet. Called only after marketing consent. */
function loadMetaPixel(): void {
  if (pixelLoaded || !META_PIXEL_ID) return
  pixelLoaded = true

  /* eslint-disable */
  ;(function (f: any, b: Document, e: string, v: string) {
    if (f.fbq) return
    const n: any = (f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    })
    if (!f._fbq) f._fbq = n
    n.push = n
    n.loaded = true
    n.version = '2.0'
    n.queue = []
    const t = b.createElement(e) as HTMLScriptElement
    t.async = true
    t.src = v
    const s = b.getElementsByTagName(e)[0]
    s.parentNode?.insertBefore(t, s)
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')
  /* eslint-enable */

  window.fbq?.('init', META_PIXEL_ID)
  window.fbq?.('track', 'PageView')
}

function marketingAllowed(): boolean {
  const { consent } = useCookieConsent()
  return consent.value.marketing
}

function analyticsAllowed(): boolean {
  const { consent } = useCookieConsent()
  return consent.value.analytics
}

/** Map internal event names to Meta standard events. */
const META_EVENT_MAP: Record<string, string> = {
  landing_view: 'ViewContent',
  signup_started: 'InitiateCheckout',
  account_created: 'CompleteRegistration',
  business_setup_completed: 'Lead',
}

export interface TrackParams {
  /** Shared dedup id for browser Pixel <-> CAPI (Lead only). */
  event_id?: string
  content_name?: string
  [key: string]: unknown
}

/**
 * Track an event across Meta Pixel + GA4. No-ops per destination when
 * consent is missing or the destination is not configured.
 */
export function track(event: string, params: TrackParams = {}): void {
  const { event_id, ...rest } = params

  // ── Meta Pixel (marketing consent) ──────────────────────────────────────
  if (marketingAllowed() && META_PIXEL_ID) {
    loadMetaPixel()
    const metaEvent = META_EVENT_MAP[event]
    if (metaEvent && window.fbq) {
      const metaParams: Record<string, unknown> = { content_name: event, ...rest }
      if (event_id) {
        window.fbq('track', metaEvent, metaParams, { eventID: event_id })
      } else {
        window.fbq('track', metaEvent, metaParams)
      }
    }
  }

  // ── GA4 via Firebase Analytics (analytics consent) ──────────────────────
  if (analyticsAllowed()) {
    try {
      const gaParams: Record<string, unknown> = { ...rest }
      if (event_id) gaParams.event_id = event_id
      if (GA_DEBUG) gaParams.debug_mode = true
      logEvent(analytics, event, gaParams)
    } catch {
      // analytics unavailable (blocked, unsupported env) — never break the app
    }
  }
}

/**
 * Initialize tracking: watch consent so the Pixel loads as soon as
 * marketing consent is granted (including right after banner accept).
 */
export function initTracking(): void {
  const { consent } = useCookieConsent()
  watch(
    () => consent.value.marketing,
    (allowed) => {
      if (allowed) loadMetaPixel()
    },
    { immediate: true },
  )
}
